import { useCallback, useEffect, useState } from 'react';

import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import {
  useExperiment,
  useFeature,
  useFeatures,
  useViewerData,
} from '@xing-com/hub';

type UsePayOrConsentStateResult = {
  shouldHoldConsentUi: boolean;
  shouldRenderPayOrConsentPlaceholder: boolean;
};

const CONSENT_CHANGE_EVENTS = new Set(['ACCEPT_ALL', 'DENY_ALL', 'SAVE']);
const USERCENTRICS_CONSENT_EVENT = 'UC_CONSENT';
const USERCENTRICS_INITIALIZED_EVENT = 'UC_UI_INITIALIZED';
const USERCENTRICS_UI_CMP_EVENT = 'UC_UI_CMP_EVENT';

async function areAllConsentsAccepted(): Promise<boolean | undefined> {
  if (typeof window === 'undefined' || !window.__ucCmp?.getConsentDetails) {
    return undefined;
  }

  try {
    const consentDetails = await window.__ucCmp.getConsentDetails();
    return consentDetails.consent.status === 'ALL_ACCEPTED';
  } catch {
    return undefined;
  }
}

export function usePayOrConsentState(): UsePayOrConsentStateResult {
  const { isLoggedIn, isLoggedOut } = useLoginState();
  const payOrConsentExperimentEnabled = useExperiment('ABACUS-546') === 'B';
  const { loading: loadingFeatures } = useFeatures();
  const isBasic = useFeature('isBasic', true);
  const activePayingMember =
    isLoggedIn && !loadingFeatures && isBasic === false;
  const { data: viewerData, loading: loadingViewerData } = useViewerData();
  const acceptedAllConsentsFromViewer =
    viewerData?.hasThirdPartyTrackingConsent ?? false;
  const [acceptedAllConsentsFromUsercentrics, setAcceptedAllConsents] =
    useState<boolean | undefined>();
  const acceptedAllConsents =
    acceptedAllConsentsFromUsercentrics ?? acceptedAllConsentsFromViewer;

  const refreshAcceptedAllConsents = useCallback((): void => {
    void areAllConsentsAccepted().then((allConsentsAccepted) => {
      if (allConsentsAccepted !== undefined) {
        setAcceptedAllConsents(allConsentsAccepted);
      }
    });
  }, []);

  useEffect(() => {
    if (isLoggedOut && payOrConsentExperimentEnabled) {
      refreshAcceptedAllConsents();
    }

    const handleUsercentricsInitialized = (): void => {
      refreshAcceptedAllConsents();
    };

    const handleUsercentricsUiEvent = (event: Event): void => {
      const cmpEvent = event as CustomEvent<{ type?: string }>;

      if (
        !cmpEvent.detail?.type ||
        !CONSENT_CHANGE_EVENTS.has(cmpEvent.detail.type)
      ) {
        return;
      }

      refreshAcceptedAllConsents();
    };

    window.addEventListener(
      USERCENTRICS_INITIALIZED_EVENT,
      handleUsercentricsInitialized
    );
    window.addEventListener(
      USERCENTRICS_UI_CMP_EVENT,
      handleUsercentricsUiEvent
    );
    window.addEventListener(
      USERCENTRICS_CONSENT_EVENT,
      refreshAcceptedAllConsents
    );

    return () => {
      window.removeEventListener(
        USERCENTRICS_INITIALIZED_EVENT,
        handleUsercentricsInitialized
      );
      window.removeEventListener(
        USERCENTRICS_UI_CMP_EVENT,
        handleUsercentricsUiEvent
      );
      window.removeEventListener(
        USERCENTRICS_CONSENT_EVENT,
        refreshAcceptedAllConsents
      );
    };
  }, [isLoggedOut, payOrConsentExperimentEnabled, refreshAcceptedAllConsents]);

  const isLoadingLoggedOutConsentData =
    isLoggedOut &&
    payOrConsentExperimentEnabled &&
    acceptedAllConsentsFromUsercentrics === undefined;
  const isLoadingLoggedInEligibilityData =
    isLoggedIn &&
    payOrConsentExperimentEnabled &&
    (loadingFeatures || loadingViewerData === true);
  const shouldUsePayOrConsentFlow =
    payOrConsentExperimentEnabled &&
    !activePayingMember &&
    !acceptedAllConsents &&
    !isLoadingLoggedOutConsentData;

  return {
    // Eligibility can depend on async viewer data or client-only Usercentrics
    // state, so we render no consent UI until the wall decision is known.
    shouldHoldConsentUi:
      isLoadingLoggedInEligibilityData || isLoadingLoggedOutConsentData,
    shouldRenderPayOrConsentPlaceholder: shouldUsePayOrConsentFlow,
  };
}
